import React, {
  memo,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useFormContext,
  useWatch,
} from 'react-hook-form';
import {
  VisitNoteDTO,
} from 'dtos';
import {
  ToolbarItemProps,
} from 'views/EMR/components/TextAreaToolbar/index';
import {
  useGetSpecificVisitNoteSectionForPatient,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import {
  EMPTY_CKE_TEXT,
  normalizeContent,
} from 'views/EMR/Settings/helper';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import SaltIconButton from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/SaltIconButton';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';
import {
  SaltType,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/VisitNoteSalt/SaltMenu';

type Props = ToolbarItemProps & {
  patientId: number;
  currentVisitNoteId: number;
};

function SameToolbarItem({
  setData,
  editor,
  id,
  patientId,
  currentVisitNoteId,
}: Props) {
  const [selectedPastVisitNoteId, setSelectedPastVisitNoteId] = useState<number | null>(null);

  const saltType: SaltType = useMemo(
    () => {
      switch (id) {
        case VisitNoteSOAPSectionNames.AdditionalHPITemplate:
          return 'subjective';
        case VisitNoteSOAPSectionNames.PhysicalExamTemplate:
          return 'objective';
        case VisitNoteSOAPSectionNames.PlanTemplate:
          return 'plan';
        case VisitNoteSOAPSectionNames.RadiologyTemplate:
          return 'radiology';
        case VisitNoteSOAPSectionNames.AdditionalAssessmentTemplate:
          return 'additionalAssessment';
        default:
          return 'unknown';
      }
    },
    [id],
  );

  const isPastVisitNoteSelected = useMemo(
    () => !isNil(selectedPastVisitNoteId),
    [selectedPastVisitNoteId],
  );

  const {
    data,
    isLoading: isLoadingVisitNoteSection,
    refetch: fetchPriorVisitNoteSection,
    isRefetching: isRefetchingVisitNoteSection,
  } = useGetSpecificVisitNoteSectionForPatient(
    selectedPastVisitNoteId ?? 0,
    patientId ?? 0,
    id,
  );

  const handleVisitNoteSectionSalt = async () => {
    await fetchPriorVisitNoteSection();
  };

  useEffect(() => {
    if (isPastVisitNoteSelected) {
      handleVisitNoteSectionSalt();
    }
  }, [selectedPastVisitNoteId]);

  const loadingOrFetchingList = [
    isLoadingVisitNoteSection,
    isRefetchingVisitNoteSection,
  ];

  const isSaltInProgress = useMemo(
    () => loadingOrFetchingList.some((i) => i),
    [...loadingOrFetchingList],
  );

  useEffect(() => {
    if (notEmptyString(data) && !isRefetchingVisitNoteSection) {
      const currentContent = editor?.getData({ rootName: 'content' });

      const newValue = compact([
        currentContent === EMPTY_CKE_TEXT
            || isEmptyString(currentContent) ? null : currentContent,
        normalizeContent(data),
      ]).join('');

      setData(newValue);

      if (isPastVisitNoteSelected) {
        setSelectedPastVisitNoteId(null);
      }
    }
  }, [data, isRefetchingVisitNoteSection]);

  return (
    <SaltIconButton
      patientId={patientId ?? 0}
      visitNoteId={currentVisitNoteId ?? 0}
      isSaltInProgress={isSaltInProgress}
      saltType={saltType}
      handleSalt={setSelectedPastVisitNoteId}
    />
  );
}

const SameToolbarItemMemoized = memo(SameToolbarItem);

export default function SameToolbarItemContainer(props: ToolbarItemProps) {
  const { control } = useFormContext<Partial<VisitNoteDTO>>();
  const [patientId, currentVisitNoteId] = useWatch({
    control,
    name: ['patientId', 'visitNoteId'],
  });

  return (
    <SameToolbarItemMemoized
      patientId={patientId ?? 0}
      currentVisitNoteId={currentVisitNoteId ?? 0}
      {...props}
    />
  );
}
